import LatLon from 'geodesy/latlon-spherical';
import OsGridRef, { LatLon as LatLon_OsGridRef } from 'geodesy/osgridref';
import Elr from './elr';


/**
 * GPS
 * For conversions see: https://www.pgc.umn.edu/apps/convert/
 * Also see: https://github.com/chrisveness/geodesy/blob/master/latlon-spherical.js
 */

// Convert Lat/Lng to Degrees & Decimal Minutes
export const getDegreesAndDecimalMinutesFromLatLng = (lat, lng) => {
	const location = new LatLon(lat, lng);
	const ddm = location.toString('dm', 4);
	const [latStr, lngStr] = ddm.split(',');

	// This function converts to the Where Am I-specific format: 0000.0000N : 0000.0000(W or E)
	const formatDDM = str => {
		let strArray = str.trim().split('°');

		let deg = strArray[0].slice(-2);
		if (strArray[0].length === 3 && strArray[0].charAt(0) !== "0") {
			deg = strArray[0].slice(-3);
		}

		strArray = strArray[1].split('′');
		const min = strArray[0].trim();
		const direction = strArray[1].trim();

		return `${deg}${min}${direction}`;
	};

	const latFormatted = formatDDM(latStr);
	const lngFormatted = formatDDM(lngStr);

	return {
		ddm: `${latFormatted} : ${lngFormatted}`
	};
}

// Convert Degrees & Decimal Minutes to Lat/Lng
export const getLatLngFromDegreesAndDecimalMinutes = (latDeg, latMin, lngDeg, lngMin, lngWOrE) => {
	// NB: No latNOrS param as in the UK it is always North
	const ddm = `${latDeg}°${latMin}’N, ${lngDeg}°${lngMin}’${lngWOrE}`;
	const [lat, lng] = LatLon.parse(ddm).toString('n', 8).split(',');

	return { lat, lng };
}

// Convert Lat/Lng to WGS84 Latitude/Longitude
// NB: This is actually Degrees Minutes Seconds?
export const getWGS84LatLngFromLatLng = (lat, lng) => {
	const location = new LatLon(lat, lng);
	const dms = location.toString('dms', 5);
	let [latStr, lngStr] = dms.split(', ');
	lngStr = lngStr.substr(1);

	return {
		wgs84Lat: `${latStr}, `,
    wgs84Lng: `${lngStr}`
	};
}

// Convert WGS84 Latitude/Longitude to Lat/Lng
// NB: Actually converting from Degrees Minutes Seconds?
export const getLatLngFromWGS84LatLng = (latDeg, latMin, latSec, lngDeg, lngMin, lngSec, lngWOrE) => {
	// NB: No latNOrS param as in the UK it is always North
	const dms = `${latDeg}°${latMin}′${latSec}″N, ${lngDeg}°${lngMin}′${lngSec}″${lngWOrE}`;
	const [lat, lng] = LatLon.parse(dms).toString('n', 8).split(',');

	return { lat, lng };
}

// Convert Lat/Lng to Decimal Degrees
// NB: Lat/Lng and Decimal Degrees are the same but DD is formatted differently in the app for some reason
// i.e. instead of 52.0370664,-0.7753201 DD is formatted as: 52.037066° N, 0.7753201° W
export const getDecimalDegreesFromLatLng = (lat, lng) => {
	const location = new LatLon(lat, lng);
	const dd = location.toString('d', 6);
	let [latStr, lngStr] = dd.split(', ');
	lngStr = lngStr.substr(1);

	return {
		dd: `${latStr}, ${lngStr}`
	};
}

// Convert Decimal Degrees to Lat/Lng
// NB: Lat/Lng and Decimal Degrees are the same but DD is formatted differently in the app for some reason
// i.e. instead of 52.0370664,-0.7753201 DD is formatted as: 52.037066° N, 0.7753201° W
export const getLatLngFromDecimalDegrees = (latDeg, lngDeg, lngWOrE) => {
	// NB: No latNOrS param as in the UK it is always North
	const dd = `${latDeg}N, ${lngDeg}${lngWOrE}`;
	const [lat, lng] = LatLon.parse(dd).toString('n', 8).split(',');

	return { lat, lng };
}


/**
 * OS Grid
 * See: https://github.com/chrisveness/geodesy/blob/master/osgridref.js
 */

// Convert Lat/Lng to National Grid Reference
export const getNationalGridReferenceFromLatLng = (lat, lng) => {
	let osGridRef = 'n/a'; // only 'n/a' if outside of the UK

	try {
		const grid = new LatLon_OsGridRef(lat, lng);
		osGridRef = grid.toOsGrid().toString();
	} finally {
		return {
			ngr: osGridRef
		};
	}
}

// Convert National Grid Reference to Lat/Lng
export const getLatLngFromNationalGridReference = (ngr) => {
	const { easting, northing } = OsGridRef.parse(ngr);

	return getLatLngFromEastingsNorthings(easting, northing);
}

// Convert Lat/Lng to Eastings/Northings
export const getEastingsNorthingsFromLatLng = (lat, lng) => {
	let eastingsNorthings = 'n/a'; // only 'n/a' if outside of the UK

	try {
		const grid = new LatLon_OsGridRef(lat, lng);
		const { easting, northing } = grid.toOsGrid();
		eastingsNorthings = `${Math.round(easting)}, ${Math.round(northing)}`;
	} finally {
		return { eastingsNorthings };
	}
}

// Convert Eastings/Northings to Lat/Lng
export const getLatLngFromEastingsNorthings = (easting, northing) => {
	const gridref = new OsGridRef(easting, northing);
	const pWgs84 = gridref.toLatLon();
	const [lat, lng] = LatLon.parse(pWgs84).toString('n', 8).split(',');

	return { lat, lng };
}

// Convert Lat/Lng to GPS Latitude/Longitude
export const getGPSLatLngFromLatLng = (lat, lng) => getWGS84LatLngFromLatLng(lat, lng);

// Convert GPS Latitude/Longitude to Lat/Lng
export const getLatLngFromGPSLatLng = (latDeg, latMin, latSec, lngDeg, lngMin, lngSec, lngWOrE) => getLatLngFromWGS84LatLng(latDeg, latMin, latSec, lngDeg, lngMin, lngSec, lngWOrE);


/**
 * ELR
 */

// Gets list of ELR data for ELR's within 1000 metres of given Lat/Lng
export const getELRsWithin1000MetresFromLatLng = (lat, lng) => {
	const elrsInRadius = Elr.getELRsInRadius([lat, lng], 1000);
	const elrs = elrsInRadius.map(elr => {
		return {
			elr: elr.elr,
			miles: `${elr.miles}m`,
			yards: `${Math.round(elr.yards)}yd`,
			chains: `${Math.round((elr.yards/22))}ch`,
			description: '',
			distanceAway: Math.round(elr.distanceToTrackInYards)
		};
	});

	return { elrs };
}

// Convert a location in ELR, Miles and Yards format to Lat/Lng
export const getLatLngFromELR = (elr, miles, yards) => {
	const { lat, lng } = Elr.getLatLngFromELR(elr, miles, yards);

	return { lat, lng };
}


/**
 * Postcode
 */

// TODO: Get the nearest postcode and address of a given Lat/Lng
export const getPostcodeAddressFromLatLng = (lat, lng) => {
	return {
		postcode: 'LU6 1SW',
    houseNo: 17,
    streetName: 'Victoria Crescent',
    town: 'Milton Keynes',
    county: 'Befordshire'
	};
}

// TODO: Convert postcode to Lat/Lng
export const getLatLngFromPostcode = postcode => {
	return {
		lat: 52.0370664,
    lng: -0.7753201
	};
}


/**
 * All Formats
 */

// Calls functions that convert lat/lng to all other formats
export const getAllFormatsFromLatLng = (lat, lng) => {
	return {
		...getDegreesAndDecimalMinutesFromLatLng(lat, lng),
		...getWGS84LatLngFromLatLng(lat, lng),
		...getDecimalDegreesFromLatLng(lat, lng),
		...getNationalGridReferenceFromLatLng(lat, lng),
		...getEastingsNorthingsFromLatLng(lat, lng),
		...getELRsWithin1000MetresFromLatLng(lat, lng),
		...getPostcodeAddressFromLatLng(lat, lng)
	}
}